import { useEffect, useState } from "react";

interface VideoPlayerProps {
  poster: string;
  title: string;
  episodeLabel: string;
}

function formatTime(seconds: number) {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function VideoPlayer({ poster, title, episodeLabel }: VideoPlayerProps) {
  const totalSeconds = 24 * 60;
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    setElapsed(0);
    setPlaying(false);
  }, [episodeLabel]);

  useEffect(() => {
    if (!playing) return;
    const timer = window.setInterval(() => {
      setElapsed((prev) => {
        if (prev >= totalSeconds) {
          setPlaying(false);
          return totalSeconds;
        }
        return prev + 5;
      });
    }, 250);
    return () => window.clearInterval(timer);
  }, [playing]);

  const percent = (elapsed / totalSeconds) * 100;

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    setElapsed(Math.round(ratio * totalSeconds));
  };

  return (
    <div className="w-full">
      <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-foreground-950">
        <img src={poster} alt={title} className="w-full h-full object-cover object-top opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/40"></div>

        <span className="absolute top-3 left-3 px-2.5 py-1 rounded bg-black/50 text-white/90 text-xs backdrop-blur flex items-center gap-1.5">
          <i className="ri-information-line"></i>
          演示播放器 · 无真实视频流
        </span>

        <div className="absolute inset-0 flex items-center justify-center">
          <button
            type="button"
            onClick={() => setPlaying((v) => !v)}
            aria-label={playing ? "暂停" : "播放"}
            className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500/95 text-white hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className={`${playing ? "ri-pause-fill" : "ri-play-fill"} text-3xl md:text-4xl`}></i>
          </button>
        </div>

        <div className="absolute bottom-0 left-0 right-0 px-3 md:px-4 pb-3 md:pb-4 pt-8 bg-gradient-to-t from-black/85 to-transparent">
          <div className="text-white text-xs md:text-sm mb-2 flex items-center justify-between gap-2">
            <span className="truncate">{title} · {episodeLabel}</span>
            <span className="whitespace-nowrap text-white/80">
              {formatTime(elapsed)} / {formatTime(totalSeconds)}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setPlaying((v) => !v)}
              className="w-8 h-8 flex items-center justify-center text-white hover:text-primary-300 transition-colors cursor-pointer"
              aria-label={playing ? "暂停" : "播放"}
            >
              <i className={`${playing ? "ri-pause-fill" : "ri-play-fill"} text-xl`}></i>
            </button>

            <div
              className="flex-1 h-1.5 rounded-full bg-white/25 cursor-pointer group relative"
              onClick={handleSeek}
            >
              <div className="h-full rounded-full bg-primary-500 relative" style={{ width: `${percent}%` }}>
                <span className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-3 h-3 rounded-full bg-primary-500 opacity-0 group-hover:opacity-100 transition-opacity"></span>
              </div>
            </div>

            <button type="button" className="w-8 h-8 hidden sm:flex items-center justify-center text-white hover:text-primary-300 transition-colors cursor-pointer" aria-label="音量">
              <i className="ri-volume-up-line text-xl"></i>
            </button>
            <button type="button" className="w-8 h-8 hidden sm:flex items-center justify-center text-white hover:text-primary-300 transition-colors cursor-pointer" aria-label="设置">
              <i className="ri-settings-3-line text-lg"></i>
            </button>
            <button type="button" className="w-8 h-8 flex items-center justify-center text-white hover:text-primary-300 transition-colors cursor-pointer" aria-label="全屏">
              <i className="ri-fullscreen-line text-xl"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}